import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';

interface Props {
  disabled?: boolean;
  isCorrect?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  onMoveDown?: () => void;
  onMoveUp?: () => void;
  showIcon?: boolean;
  text: string;
}

const ReaderBlockSequencingOption = ({
  disabled,
  isCorrect,
  isFirst,
  isLast,
  onMoveDown,
  onMoveUp,
  showIcon,
  text
}: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <div className="option-wrapper">
      <div className={`option-inner${disabled ? ' disabled' : ''}`}>
        <div className="option-text">{text}</div>

        {!disabled && onMoveUp && onMoveDown && (
          <div className="move-buttons">
            <Button
              aria-label={messages.commonMoveUp}
              category="lighter"
              disabled={isFirst}
              icon="arrow-up"
              onClick={(e) => {
                e.stopPropagation(); // avoid odd interactions with drag
                onMoveUp();
              }}
              size="small"
              type="button"
            />
            <Button
              aria-label={messages.commonMoveDown}
              category="lighter"
              disabled={isLast}
              icon="arrow-down"
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown();
              }}
              size="small"
              type="button"
            />
          </div>
        )}

        {showIcon ? (
          <div className="result-icon">
            {isCorrect ? (
              <span
                aria-label={messages.commonCorrectAnswer}
                data-tooltip={messages.commonCorrectAnswer}
              >
                <Icon
                  icon="check"
                  style={{ color: theme.successStandaloneColor }}
                />
              </span>
            ) : (
              <span
                aria-label={messages.commonIncorrectAnswer}
                data-tooltip={messages.commonIncorrectAnswer}
              >
                <Icon icon="times" style={{ color: theme.dangerColor }} />
              </span>
            )}
          </div>
        ) : null}
      </div>

      <style jsx>{`
        .option-wrapper {
          cursor: ${disabled ? 'default' : 'move'};
          margin: 5px 0;
          text-align: center;
          width: 100%;
        }
        .option-inner {
          background-color: white;
          border: 2px dashed ${theme.lightGreyDark};
          border-radius: 5px;
          padding: 8px 80px 8px 8px;
          position: relative;
          transition: border-color 0.3s, color 0.3s;
        }
        .option-inner:focus,
        .option-inner:hover {
          border-color: ${theme.textColorLighter};
          color: ${theme.textColor};
        }
        .option-inner.disabled,
        .option-inner.disabled:focus,
        .option-inner.disabled:hover {
          border: 1px solid ${theme.lightGreyDark};
          color: inherit;
          cursor: default;
          opacity: 0.7;
        }
        .option-text {
          min-height: 1em;
        }
        .move-buttons {
          align-items: center;
          display: flex;
          flex-direction: row;
          gap: 5px;
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
        }
        .result-icon {
          font-size: 1.2rem;
          position: absolute;
          right: 10px;
          top: 11px;
        }
      `}</style>
    </div>
  );
};

export default ReaderBlockSequencingOption;
